import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Eye, Sparkles, Check, Shield } from 'lucide-react';
import { rtdbNFLService as NFLService } from '../rtdb';
import { useAddToChat } from '../../../../components/chat/chatContext';
import { getBrazilianStatus } from '../../../../utils/detectBrazil';
import { nflDashPtBR } from '../../../../components/mappings/sports-dash-mappings/nfl-dash.pt-br';
import PredictButton from "../../../../components/dashboards/components/shared/PredictButton";
import XPicksButton from "../../../../components/dashboards/components/shared/XPicksButton";
import ViewOddsButton from "../../../../components/dashboards/components/shared/ViewOddsButton";
import PredictionBox from "../../../../components/dashboards/components/shared/PredictionBox";

const NFLDesktopGameCard = memo(({
  game,
  isSelected,
  isFrozen = false,
  onToggleSelection,
  prediction,
  onGameSelect,
  onViewOdds
}) => {
  // Internationalization
  const isBrazilian = getBrazilianStatus();
  const t = (text) => isBrazilian ? (nflDashPtBR[text] || text) : text;

  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `${t('Analyze this NFL prediction:')}

${teams?.away?.name} @ ${teams?.home?.name}

${t('Prediction:')} ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% ${t('confidence')})` : ''}
${t('Confidence Level:')} ${prediction.confidence}
${prediction.reason ? `${t('Reasoning:')} ${prediction.reason}` : ''}

${t('Can you provide a deeper analysis of this matchup?')}`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!game) return null;

  const { teams, scores } = game;
  // NFL uses game property instead of fixture
  const gameInfo = game.game || game.fixture || {};

  // Check for various live status codes including NFL specific ones
  const statusShort = gameInfo?.status?.short;
  const isLive = ['Q1', 'Q2', 'Q3', 'Q4', 'HT', 'OT', '1H', '2H', 'ET', 'BT', 'P', 'SUSP', 'INT', 'LIVE'].includes(statusShort);
  const isFinished = ['FT', 'AOT', 'AET', 'PEN'].includes(statusShort);
  const isUpcoming = !isLive && !isFinished;
  const effectiveFrozen = isFrozen || Boolean(prediction);

  // Format game time to ET
  const formatGameTime = () => {
    if (isLive) return t('LIVE');
    if (isFinished) return t('Final');

    // Handle NFL date format (date and time fields)
    if (gameInfo?.date?.date && gameInfo?.date?.time) {
      try {
        const formattedTime = NFLService.formatTime(gameInfo.date.date + 'T' + gameInfo.date.time);
        return formattedTime;
      } catch (e) {
        return t('TBD');
      }
    } else if (gameInfo?.date) {
      try {
        const formattedTime = NFLService.formatTime(gameInfo.date);
        return formattedTime;
      } catch (e) {
        return t('TBD');
      }
    }

    return t('TBD');
  };

  // Get quarter display for live games
  const getQuarterDisplay = () => {
    if (!isLive) return null;

    // NFL uses quarter status directly
    if (['Q1', 'Q2', 'Q3', 'Q4', 'HT', 'OT'].includes(statusShort)) {
      return statusShort;
    }

    const elapsed = gameInfo?.status?.elapsed;
    if (elapsed) {
      if (elapsed <= 15) return 'Q1';
      if (elapsed <= 30) return 'Q2';
      if (elapsed <= 45) return 'Q3';
      if (elapsed <= 60) return 'Q4';
      return 'OT';
    }

    return 'In Progress';
  };

  // Get team division
  const getTeamDivision = (team) => {
    if (!team?.conference || !team?.division) return null;
    return `${team.conference} ${team.division}`;
  };

  return (
    <div className={`relative bg-white/[0.03] p-4 hover:bg-white/[0.05] transition-all duration-200 border ${
      isSelected ? 'border-blue-500/50 bg-blue-500/10' : 'border-white/[0.05] hover:border-white/10'
    } active:bg-white/[0.07]`}>
      {/* Status Badge - Top Right */}
      {(isLive || isFinished) && (
        <div className="absolute top-3 right-3 z-10">
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500 animate-pulse' : 'bg-gray-700'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />}
            <span className="text-[10px] font-black text-white uppercase">
              {isLive ? getQuarterDisplay() : 'FINAL'}
            </span>
          </div>
        </div>
      )}

      {/* Division/Conference Label with Week */}
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-3 h-3 text-blue-400" />
        <span className="text-xs text-white/50 font-medium uppercase tracking-wider">
          {getTeamDivision(teams?.home) || getTeamDivision(teams?.away) || 'NFL'}
          {gameInfo?.week && (
            <span className="ml-2 text-blue-300">• Week {gameInfo.week}</span>
          )}
        </span>
      </div>

      {/* Teams and Scores */}
      <div className="space-y-2.5">
        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 flex-1">
            {teams?.away?.logo && (
              <img
                src={teams.away.logo}
                alt={teams.away.name}
                className="w-8 h-8 object-contain"
                loading="lazy"
              />
            )}
            <span className="text-sm font-semibold text-white truncate">
              {teams?.away?.name || 'Away Team'}
            </span>
          </div>
          <span className="text-lg font-bold text-white tabular-nums">
            {isLive || isFinished ? (scores?.away?.total ?? scores?.away ?? 0) : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 flex-1">
            {teams?.home?.logo && (
              <img
                src={teams.home.logo}
                alt={teams.home.name}
                className="w-8 h-8 object-contain"
                loading="lazy"
              />
            )}
            <span className="text-sm font-semibold text-white truncate">
              {teams?.home?.name || 'Home Team'}
            </span>
          </div>
          <span className="text-lg font-bold text-white tabular-nums">
            {isLive || isFinished ? (scores?.home?.total ?? scores?.home ?? 0) : '-'}
          </span>
        </div>
      </div>

      {/* Game Time and Venue */}
      {isUpcoming && (
        <div className="mt-3 pt-2 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-white/50">
              <Clock size={12} />
              <span className="text-xs">
                {formatGameTime()}
              </span>
            </div>
            {gameInfo?.venue?.name && (
              <span className="text-xs text-white/40 truncate">
                {gameInfo.venue.name}
              </span>
            )}
          </div>
        </div>
      )}


      {/* Prediction Result */}
      {prediction && (
        <div className="pt-2">
          <PredictionBox
            prediction={prediction}
            onAnalyze={handleAnalyzeInChat}
            variant="desktop"
            t={t}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {/* xPicks Button */}
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.away?.name,
                homeTeamId: teams?.home?.id,
                awayTeamId: teams?.away?.id,
                leagueId: game?.league?.id || 1,
                date: gameInfo?.date?.date || gameInfo?.date,
                time: formatGameTime(),
                gameId: gameInfo?.id
              }}
              sport="nfl"
              label="XPICKS"
            />
          </div>
        )}

        {/* Prediction Selection Button */}
        {(isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={effectiveFrozen ? 'COOLDOWN' : isSelected ? 'SELECTED' : isLive ? 'PREDICT LIVE' : 'PREDICT'}
              icon={effectiveFrozen ? Clock : isSelected ? Check : Sparkles}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={effectiveFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!effectiveFrozen) {
                  onToggleSelection(game);
                }
              }}
            />
          </div>
        )}

        {/* View Odds Button */}
        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label="VIEW ODDS"
              onClick={() => onViewOdds(game)}
              className="w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
});

NFLDesktopGameCard.displayName = 'NFLGameCardWithPrediction';

export default NFLDesktopGameCard;
